/**
 * State machine for one AI support conversation: draft → sending → streaming
 * → idle (complete / failed / aborted), plus every error-code path (plan §6.2).
 *
 * Pure and deterministic: no I/O, no timers, never calls `Date.now()` — time
 * arrives in the action (`now`). Never mutates; a no-op returns the same state
 * object so React can bail out. Framework-free (copied to next-mapit as TS);
 * driven by `useReducer` in `hooks/useAiChat.js`.
 */

import { isChatApiError } from "./errors";
import {
  CONVERSATION_STATUS,
  ERROR_CODES,
  MESSAGE_ROLES,
  MESSAGE_STATUS,
} from "./protocol";

/**
 * `state.phase`. `sending`: the request is in flight and no `meta` arrived yet.
 * `streaming`: after `meta`, until a terminal action.
 */
export const CHAT_PHASES = Object.freeze({
  IDLE: "idle",
  SENDING: "sending",
  STREAMING: "streaming",
});

/** `state.notice.kind`: an inline notice at the top of the message list (plan §3.7). */
export const NOTICE_KINDS = Object.freeze({
  /** 853. Carries `until` (epoch ms) for the countdown. */
  RATE_LIMITED: "rateLimited",
  /** 852. WhatsApp button; the composer stays disabled until dismissed. */
  UNAVAILABLE: "unavailable",
  /** 854. */
  IN_PROGRESS: "inProgress",
  /** Transport failure (the failed message has its own Retry). */
  NETWORK: "network",
  /** 855. Shown wherever the panel lands after `goHome`. */
  NOT_FOUND: "notFound",
  /** 857 (the client limit was stale). */
  TOO_LONG: "tooLong",
  /** Any other code. Carries the server's localized `message` ("" → `errors.generic`). */
  GENERIC: "generic",
});

/** `state.signal`: a one-shot instruction for the hook/panel, consumed with `clearSignal()`. */
export const CHAT_SIGNALS = Object.freeze({
  /** 851 (the launcher should disappear) and 857 (refresh `maxMessageChars`). */
  REFETCH_ELIGIBILITY: "refetchEligibility",
  /** 855: the conversation is gone — show home and refetch the list. */
  GO_HOME: "goHome",
});

/** `getSendBlocker()` results. */
export const SEND_BLOCKERS = Object.freeze({
  BUSY: "busy",
  CLOSED: "closed",
  COOLDOWN: "cooldown",
  UNAVAILABLE: "unavailable",
});

/** Cooldown when 853 arrives without a usable `Retry-After` (C22). */
export const RATE_LIMIT_FALLBACK_SECONDS = 30;

/** Action `type`s. */
export const CHAT_ACTIONS = Object.freeze({
  START_DRAFT: "aiChat/startDraft",
  HYDRATE: "aiChat/hydrate",
  SEND_STARTED: "aiChat/sendStarted",
  META: "aiChat/meta",
  DELTA_BATCH: "aiChat/deltaBatch",
  DONE: "aiChat/done",
  STREAM_ERROR: "aiChat/streamError",
  STREAM_ENDED_WITHOUT_DONE: "aiChat/streamEndedWithoutDone",
  API_ERROR: "aiChat/apiError",
  ABORTED: "aiChat/aborted",
  FEEDBACK_SAVED: "aiChat/feedbackSaved",
  DISMISS_NOTICE: "aiChat/dismissNotice",
  CLEAR_SIGNAL: "aiChat/clearSignal",
  CLEAR_RESTORE_TEXT: "aiChat/clearRestoreText",
});

/**
 * @typedef {Object} ChatConversation
 * @property {string | null} id null while a draft (the first `POST /chat` creates it).
 * @property {string} title "" for a draft (the UI shows `conversation.newTitle`).
 * @property {"open" | "closed"} status
 * @property {null | "new_conversation" | "idle"} closedReason
 * @property {boolean} isDraft true until `meta` names the real conversation.
 */

/**
 * @typedef {Object} ChatTurnError
 * @property {import("./errors").ChatErrorCode | null} code
 * @property {string} message Server's localized text; may be "".
 * @property {boolean} retryable Whether to offer Retry.
 */

/**
 * @typedef {Object} ChatMessage
 * @property {string} id Server id; the temp id until `meta` replaces it.
 * @property {string} [tempId] Only on messages sent from this tab. Never
 *   changes, so `tempId ?? id` is the stable React key.
 * @property {"user" | "assistant"} role
 * @property {string} content
 * @property {"complete" | "streaming" | "aborted" | "failed" | "interrupted"} status
 * @property {string | null} createdAt ISO string.
 * @property {{ rating: "up" | "down" } | null} feedback
 * @property {{ reason: string, whatsappUrl: string } | null} escalation
 * @property {Object | null} debug `done.debug` (AI developers only; never from the server list).
 * @property {"stop" | "length" | null} finishReason From `done`.
 * @property {ChatTurnError | null} error Why a live answer failed; null when loaded from the server.
 */

/**
 * @typedef {Object} ChatNotice
 * @property {string} kind One of `NOTICE_KINDS`.
 * @property {number} [until] `rateLimited` only: epoch ms when sending is allowed again.
 * @property {string} [message] `generic` only: the server's localized message (may be "").
 */

/**
 * @typedef {Object} LivePair
 * @property {string} userTempId
 * @property {string} assistantTempId
 */

/**
 * @typedef {Object} ChatState
 * @property {ChatConversation | null} conversation null = nothing open (home).
 * @property {ChatMessage[]} messages Oldest first.
 * @property {"idle" | "sending" | "streaming"} phase
 * @property {LivePair | null} live The turn in flight. Stream actions arriving
 *   without one (e.g. after the view changed) are ignored.
 * @property {ChatNotice | null} notice
 * @property {number | null} cooldownUntil Epoch ms (853). Sending is blocked
 *   before it, even after the notice is dismissed.
 * @property {boolean} available false after 852 until the next `meta` (header status dot).
 * @property {string | null} signal One of `CHAT_SIGNALS`.
 * @property {string | null} restoreText Text the composer should take back:
 *   a send rejected before streaming, or the prefill of a new draft.
 * @property {string} pendingDraft Unsent text kept by 855/856; the next
 *   `startDraft()` without an argument prefills it.
 */

const { IDLE, SENDING, STREAMING } = CHAT_PHASES;

/** @type {ChatState} */
export const initialState = Object.freeze({
  conversation: null,
  messages: Object.freeze([]),
  phase: IDLE,
  live: null,
  notice: null,
  cooldownUntil: null,
  available: true,
  signal: null,
  restoreText: null,
  pendingDraft: "",
});

// ---------------------------------------------------------------------------
// Action creators
// ---------------------------------------------------------------------------

/**
 * Opens an empty draft. No request is sent until the first message.
 *
 * @param {string} [prefill] Composer text. Omitted → `state.pendingDraft`
 *   (text a 855/856 couldn't send); pass `""` to start empty.
 */
export const startDraft = (prefill) => ({
  type: CHAT_ACTIONS.START_DRAFT,
  prefill,
});

/**
 * Loads a conversation from `GET /conversations/:id`. Assistant messages still
 * `streaming` become `interrupted` (it isn't our live stream). Ignored while
 * that same conversation is live in this tab (C17).
 *
 * @param {{ conversation: Object, messages: Object[] }} body
 */
export const hydrate = ({ conversation, messages } = {}) => ({
  type: CHAT_ACTIONS.HYDRATE,
  conversation,
  messages,
});

/**
 * Appends the user message and an empty `streaming` assistant message (the
 * typing indicator). Starts a draft when nothing is open. Ignored unless the
 * phase is `idle` and `text` is non-empty.
 *
 * @param {{ tempUserId: string, tempAssistantId: string, text: string, now: number }} payload
 */
export const sendStarted = ({ tempUserId, tempAssistantId, text, now }) => ({
  type: CHAT_ACTIONS.SEND_STARTED,
  tempUserId,
  tempAssistantId,
  text,
  now,
});

/** SSE `meta`: real ids for the live pair; a draft becomes a real conversation. */
export const meta = (data) => ({ type: CHAT_ACTIONS.META, data });

/** Batched SSE `delta` text, appended to the live answer. */
export const deltaBatch = (text) => ({ type: CHAT_ACTIONS.DELTA_BATCH, text });

/** SSE `done`: the answer is `complete` (also for `finishReason: "length"`). */
export const done = (data) => ({ type: CHAT_ACTIONS.DONE, data });

/**
 * SSE `error` (incl. the client's `parse`): the answer is `failed`, partial
 * text kept. Code 852 also shows the `unavailable` notice.
 */
export const streamError = (data) => ({
  type: CHAT_ACTIONS.STREAM_ERROR,
  data,
});

/** The stream closed without `done`/`error`: `failed`, retryable. */
export const streamEndedWithoutDone = () => ({
  type: CHAT_ACTIONS.STREAM_ENDED_WITHOUT_DONE,
});

/**
 * A rejected `streamChat` (non-2xx envelope or transport failure), or an app
 * code the hook maps from another request (e.g. 855 when opening). `message`
 * is taken only from a `ChatApiError`, so a stray exception's text never
 * reaches the UI.
 *
 * @param {import("./errors").ChatApiError | { code?: any, retryAfterSeconds?: number | null }} error
 * @param {number} now Epoch ms (853 cooldown).
 */
export const apiError = (error, now) => ({
  type: CHAT_ACTIONS.API_ERROR,
  code: error?.code ?? null,
  message: isChatApiError(error) ? error.message : "",
  retryAfterSeconds: error?.retryAfterSeconds ?? null,
  now,
});

/** Stop: the answer is `aborted`, partial text kept (the server saves it too). */
export const aborted = () => ({ type: CHAT_ACTIONS.ABORTED });

/** `POST /messages/:id/feedback` succeeded. */
export const feedbackSaved = ({ messageId, rating }) => ({
  type: CHAT_ACTIONS.FEEDBACK_SAVED,
  messageId,
  rating,
});

export const dismissNotice = () => ({ type: CHAT_ACTIONS.DISMISS_NOTICE });

/** The hook/panel acted on `state.signal`. */
export const clearSignal = () => ({ type: CHAT_ACTIONS.CLEAR_SIGNAL });

/** The composer took `state.restoreText`. */
export const clearRestoreText = () => ({
  type: CHAT_ACTIONS.CLEAR_RESTORE_TEXT,
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Why the composer can't send right now, or null when it can. A missing
 * conversation isn't a blocker: `sendStarted` opens a draft.
 *
 * @param {ChatState} state
 * @param {number} now Epoch ms.
 * @returns {string | null} One of `SEND_BLOCKERS`, or null.
 */
export function getSendBlocker(state, now) {
  if (state.phase !== IDLE) return SEND_BLOCKERS.BUSY;
  if (state.conversation?.status === CONVERSATION_STATUS.CLOSED) {
    return SEND_BLOCKERS.CLOSED;
  }
  if (state.cooldownUntil !== null && now < state.cooldownUntil) {
    return SEND_BLOCKERS.COOLDOWN;
  }
  if (state.notice?.kind === NOTICE_KINDS.UNAVAILABLE) {
    return SEND_BLOCKERS.UNAVAILABLE;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

// Notices about the user rather than the conversation survive a view change.
const GLOBAL_NOTICES = [NOTICE_KINDS.RATE_LIMITED, NOTICE_KINDS.UNAVAILABLE];

const newDraft = () => ({
  id: null,
  title: "",
  status: CONVERSATION_STATUS.OPEN,
  closedReason: null,
  isDraft: true,
});

/** Common reset when another conversation (or none) replaces the current one. */
const leaveConversation = (state) => ({
  ...state,
  conversation: null,
  messages: [],
  phase: IDLE,
  live: null,
  notice:
    state.notice && GLOBAL_NOTICES.includes(state.notice.kind)
      ? state.notice
      : null,
  // "Go home" was about the conversation being left.
  signal: state.signal === CHAT_SIGNALS.GO_HOME ? null : state.signal,
  restoreText: null,
  pendingDraft: "",
});

/** Ends the live turn. */
const settle = (state) => ({ ...state, phase: IDLE, live: null });

const updateLiveAssistant = (state, update) => ({
  ...state,
  messages: state.messages.map((message) =>
    message.tempId === state.live.assistantTempId ? update(message) : message,
  ),
});

/** Removes the live pair: the server rejected it before saving anything. */
const dropLive = (state) => {
  if (!state.live) return state;
  const { userTempId, assistantTempId } = state.live;
  return settle({
    ...state,
    messages: state.messages.filter(
      (message) =>
        message.tempId !== userTempId && message.tempId !== assistantTempId,
    ),
  });
};

/** The live answer failed; the user message and any partial text stay. */
const failLive = (state, error) =>
  state.live
    ? settle(
        updateLiveAssistant(state, (message) => ({
          ...message,
          status: MESSAGE_STATUS.FAILED,
          error,
        })),
      )
    : state;

const failureNotice = (code, message) =>
  code === ERROR_CODES.NETWORK
    ? { kind: NOTICE_KINDS.NETWORK }
    : { kind: NOTICE_KINDS.GENERIC, message };

const cooldownSeconds = (retryAfterSeconds) =>
  Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? retryAfterSeconds
    : RATE_LIMIT_FALLBACK_SECONDS;

function onStartDraft(state, { prefill }) {
  // A draft exists only here: text handed back to it that no composer took
  // (the 853 / 852 / 854 / 857 came after Back) moves to the new draft
  const leftover = state.conversation?.isDraft ? state.restoreText : null;
  const text =
    typeof prefill === "string" ? prefill : state.pendingDraft || leftover;
  return {
    ...leaveConversation(state),
    conversation: newDraft(),
    restoreText: text || null,
  };
}

function onHydrate(state, { conversation, messages }) {
  const current = state.conversation;
  if (
    state.phase !== IDLE &&
    current &&
    !current.isDraft &&
    current.id === conversation?.id
  ) {
    return state;
  }

  // Fields only this tab knows (the stable key, `done.debug`) survive
  // re-opening a conversation that was streamed here.
  const local = new Map(state.messages.map((message) => [message.id, message]));
  const fromServer = (message) => {
    const known = local.get(message.id);
    const interrupted =
      message.role === MESSAGE_ROLES.ASSISTANT &&
      message.status === MESSAGE_STATUS.STREAMING;
    return {
      ...message,
      ...(known?.tempId ? { tempId: known.tempId } : null),
      status: interrupted ? MESSAGE_STATUS.INTERRUPTED : message.status,
      feedback: message.feedback ?? null,
      escalation: message.escalation ?? null,
      debug: message.debug ?? known?.debug ?? null,
      finishReason: message.finishReason ?? known?.finishReason ?? null,
      error: null,
    };
  };

  return {
    ...leaveConversation(state),
    conversation: conversation ? { ...conversation, isDraft: false } : null,
    messages: (messages || []).map(fromServer),
  };
}

function onSendStarted(state, { tempUserId, tempAssistantId, text, now }) {
  if (state.phase !== IDLE || !text) return state;
  const common = {
    createdAt: Number.isFinite(now) ? new Date(now).toISOString() : null,
    feedback: null,
    escalation: null,
    debug: null,
    finishReason: null,
    error: null,
  };
  return {
    ...state,
    conversation: state.conversation || newDraft(),
    messages: [
      ...state.messages,
      {
        ...common,
        id: tempUserId,
        tempId: tempUserId,
        role: MESSAGE_ROLES.USER,
        content: text,
        status: MESSAGE_STATUS.COMPLETE,
      },
      {
        ...common,
        id: tempAssistantId,
        tempId: tempAssistantId,
        role: MESSAGE_ROLES.ASSISTANT,
        content: "",
        status: MESSAGE_STATUS.STREAMING,
      },
    ],
    phase: SENDING,
    live: { userTempId: tempUserId, assistantTempId: tempAssistantId },
    notice: null,
    cooldownUntil: null,
    restoreText: null,
    pendingDraft: "",
  };
}

function onMeta(state, { data }) {
  if (!state.live) return state;
  const { userTempId, assistantTempId } = state.live;
  const info = data?.conversation;
  const realId = (message) => {
    if (message.tempId === userTempId) return data?.userMessageId;
    if (message.tempId === assistantTempId) return data?.assistantMessageId;
    return null;
  };
  return {
    ...state,
    conversation: {
      ...state.conversation,
      id: info?.id ?? state.conversation.id,
      title: info?.title ?? state.conversation.title,
      status: CONVERSATION_STATUS.OPEN,
      closedReason: null,
      isDraft: false,
    },
    messages: state.messages.map((message) => {
      const id = realId(message);
      return id ? { ...message, id } : message;
    }),
    phase: STREAMING,
    available: true,
  };
}

function onDeltaBatch(state, { text }) {
  if (!state.live || !text) return state;
  return {
    ...updateLiveAssistant(state, (message) => ({
      ...message,
      content: message.content + text,
    })),
    phase: STREAMING,
  };
}

function onDone(state, { data }) {
  if (!state.live) return state;
  return settle(
    updateLiveAssistant(state, (message) => ({
      ...message,
      status: MESSAGE_STATUS.COMPLETE,
      escalation: data?.escalation ?? null,
      debug: data?.debug ?? null,
      finishReason: data?.finishReason ?? null,
    })),
  );
}

function onStreamError(state, { data }) {
  const code = data?.code ?? ERROR_CODES.PROVIDER_ERROR;
  const failed = failLive(state, {
    code,
    message: typeof data?.message === "string" ? data.message : "",
    retryable: data?.retryable !== false,
  });
  // 852 also arrives over SSE (no profile could answer before the first
  // delta): the answer stays failed (the server saved it) and the user gets
  // the same notice as a 852 before the stream.
  if (failed === state || code !== ERROR_CODES.UNAVAILABLE) return failed;
  return {
    ...failed,
    notice: { kind: NOTICE_KINDS.UNAVAILABLE },
    available: false,
  };
}

function onAborted(state) {
  if (!state.live) return state;
  return settle(
    updateLiveAssistant(state, (message) => ({
      ...message,
      status: MESSAGE_STATUS.ABORTED,
    })),
  );
}

function onApiError(state, { code, message, retryAfterSeconds, now }) {
  const failed = () => ({
    ...failLive(state, { code, message, retryable: true }),
    notice: failureNotice(code, message),
  });

  // After `meta` the server owns the turn: only the transport can fail now.
  if (state.phase === STREAMING) return failed();

  const liveUser =
    state.live &&
    state.messages.find((m) => m.tempId === state.live.userTempId);
  const text = liveUser ? liveUser.content : "";
  // Rejected before streaming, so nothing was saved: the pair goes away.
  const dropped = dropLive(state);
  const rejected = { ...dropped, restoreText: text || state.restoreText };

  switch (code) {
    case ERROR_CODES.NOT_ELIGIBLE:
      return {
        ...rejected,
        signal: CHAT_SIGNALS.REFETCH_ELIGIBILITY,
        notice: { kind: NOTICE_KINDS.GENERIC, message },
      };
    case ERROR_CODES.UNAVAILABLE:
      return {
        ...rejected,
        notice: { kind: NOTICE_KINDS.UNAVAILABLE },
        available: false,
      };
    case ERROR_CODES.RATE_LIMITED: {
      const until = now + cooldownSeconds(retryAfterSeconds) * 1000;
      return {
        ...rejected,
        notice: { kind: NOTICE_KINDS.RATE_LIMITED, until },
        cooldownUntil: until,
      };
    }
    case ERROR_CODES.STREAM_IN_PROGRESS:
      return { ...rejected, notice: { kind: NOTICE_KINDS.IN_PROGRESS } };
    case ERROR_CODES.CONVERSATION_NOT_FOUND:
      return {
        ...leaveConversation(state),
        signal: CHAT_SIGNALS.GO_HOME,
        notice: { kind: NOTICE_KINDS.NOT_FOUND },
        pendingDraft: text || state.pendingDraft,
      };
    case ERROR_CODES.CONVERSATION_CLOSED:
      // The read-only footer is the message; the text waits for a new draft.
      return {
        ...dropped,
        conversation: state.conversation && {
          ...state.conversation,
          status: CONVERSATION_STATUS.CLOSED,
        },
        notice: null,
        pendingDraft: text || state.pendingDraft,
      };
    case ERROR_CODES.MESSAGE_TOO_LONG:
      return {
        ...rejected,
        signal: CHAT_SIGNALS.REFETCH_ELIGIBILITY,
        notice: { kind: NOTICE_KINDS.TOO_LONG },
      };
    case ERROR_CODES.DUPLICATE_MESSAGE:
      // A double submit: the first copy already reached the server.
      return dropped;
    default:
      // Network or unknown: whether the server saved the message is unknown,
      // so keep it and offer Retry.
      return failed();
  }
}

function onFeedbackSaved(state, { messageId, rating }) {
  const index = state.messages.findIndex((message) => message.id === messageId);
  if (index === -1) return state;
  const messages = state.messages.slice();
  messages[index] = { ...messages[index], feedback: { rating } };
  return { ...state, messages };
}

/**
 * @param {ChatState} state
 * @param {{ type: string }} action
 * @returns {ChatState}
 */
export function chatReducer(state = initialState, action) {
  switch (action?.type) {
    case CHAT_ACTIONS.START_DRAFT:
      return onStartDraft(state, action);
    case CHAT_ACTIONS.HYDRATE:
      return onHydrate(state, action);
    case CHAT_ACTIONS.SEND_STARTED:
      return onSendStarted(state, action);
    case CHAT_ACTIONS.META:
      return onMeta(state, action);
    case CHAT_ACTIONS.DELTA_BATCH:
      return onDeltaBatch(state, action);
    case CHAT_ACTIONS.DONE:
      return onDone(state, action);
    case CHAT_ACTIONS.STREAM_ERROR:
      return onStreamError(state, action);
    case CHAT_ACTIONS.STREAM_ENDED_WITHOUT_DONE:
      return failLive(state, {
        code: ERROR_CODES.NETWORK,
        message: "",
        retryable: true,
      });
    case CHAT_ACTIONS.API_ERROR:
      return onApiError(state, action);
    case CHAT_ACTIONS.ABORTED:
      return onAborted(state);
    case CHAT_ACTIONS.FEEDBACK_SAVED:
      return onFeedbackSaved(state, action);
    case CHAT_ACTIONS.DISMISS_NOTICE:
      return state.notice === null ? state : { ...state, notice: null };
    case CHAT_ACTIONS.CLEAR_SIGNAL:
      return state.signal === null ? state : { ...state, signal: null };
    case CHAT_ACTIONS.CLEAR_RESTORE_TEXT:
      return state.restoreText === null
        ? state
        : { ...state, restoreText: null };
    default:
      return state;
  }
}
